/**
 * 광고 성과 페이지의 순수 계산 — 기간 프리셋(KST)과 분해 표 묶기.
 *
 * ⚠️ 이 파일은 런타임 import 를 두지 않는다 — `scripts/ad-performance.test.mjs` 가 노드에서 바로 읽는다
 *    (`sidebarNav.ts` 와 같은 규칙. 타입 import 는 지워지므로 괜찮다).
 *
 * 묶기 규칙(백엔드 `AdPerformanceAdRow` docstring):
 * - 행의 `roas`·`ctr`·`cpc`·`cpm` 은 플랫폼이 준 **행별 값**이라 평균 내지 않는다. 합을 먼저 내고 나눈다.
 * - 전환은 채널 중립 쌍 `conversions`/`conversion_value` 만 읽는다(설계 §3.4 — `purchases` 는 비-Meta 에서 0).
 * - 전환 칸이 null 인 행이 섞이면 그 묶음의 전환은 "모름"(null)이다 — 0 으로 채우지 않는다.
 */
import type { AdPerformanceAdRow } from '@/api/schemas'

// =============================================================================
// 기간
// =============================================================================

/** 기간 프리셋(일). 상한은 서버와 같은 92일(`MAX_PERFORMANCE_RANGE_DAYS`)이라 90 이 최대다. */
export const PERIOD_PRESETS = [7, 30, 90] as const
export type PeriodDays = (typeof PERIOD_PRESETS)[number]

export function parsePeriodDays(value: unknown): PeriodDays {
  const n = Number(value)
  return (PERIOD_PRESETS as readonly number[]).includes(n) ? (n as PeriodDays) : 30
}

/** 지금 KST 달력일 'YYYY-MM-DD'. 서버의 `kst_today()` 와 같은 기준 — 브라우저 시간대에 기대지 않는다. */
export function kstToday(now: Date = new Date()): string {
  // 로케일 포맷 문자열(en-CA 등)에 기대지 않고 부분을 조립한다 — ICU 빌드마다 구분자가 다를 수 있다(리뷰 Info).
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')}`
}

/** 'YYYY-MM-DD' 에 일수를 더한다 — UTC 자정 기준 계산이라 시간대·서머타임 영향이 없다. */
export function shiftDate(iso: string, days: number): string {
  const t = Date.parse(`${iso}T00:00:00Z`) + days * 86_400_000
  return new Date(t).toISOString().slice(0, 10)
}

/**
 * 최근 N일 = 오늘(KST)을 포함한 N일. 오늘·어제는 서버가 `pending`(확정 전)으로 돌려주고 화면은 흐리게 그린다.
 */
export function periodRange(days: number, today: string = kstToday()): { date_from: string; date_to: string } {
  return { date_from: shiftDate(today, -(days - 1)), date_to: today }
}

// =============================================================================
// 분해 표
// =============================================================================

export type BreakdownLevel = 'campaign' | 'adset' | 'ad'

export interface BreakdownRow {
  key: string
  /** 화면 이름 — 기간 중 **가장 최근 날** 행의 이름(중간에 이름을 바꾼 광고). 없으면 ID */
  name: string
  /** 광고그룹·광고 행에서 상위 캠페인 이름 — 같은 이름의 광고를 구분하는 부연 */
  parent: string | null
  spend: number
  impressions: number
  clicks: number
  /** 전환 칸이 null 인 행이 하나라도 섞이면 null(모름) */
  conversions: number | null
  conversion_value: number | null
  roas: number | null
  ctr: number | null
  cpc: number | null
  /** 행 통화가 섞였거나 모르는 행이 있으면 null — 금액·ROAS 를 '—' 로 보인다 */
  currency: string | null
}

function levelId(row: AdPerformanceAdRow, level: BreakdownLevel): string | null {
  // 채널 중립 ID 를 먼저, 없으면 Meta 레거시 칸(마이그 123 이전 행 대비).
  if (level === 'campaign') return row.campaign_id ?? row.meta_campaign_id ?? null
  if (level === 'adset') return row.adset_id ?? row.meta_adset_id ?? null
  return row.ad_id ?? row.meta_ad_id ?? null
}

function levelName(row: AdPerformanceAdRow, level: BreakdownLevel): string | null {
  if (level === 'campaign') return row.campaign_name ?? null
  if (level === 'adset') return row.adset_name ?? null
  return row.ad_name ?? null
}

/** ID 가 없는 행을 버리지 않는다 — 버리면 표 합이 KPI 합보다 조용히 작아진다. */
const UNKNOWN_KEY = '__unknown__'

/**
 * 광고×일 행을 캠페인/광고그룹/광고 단위로 묶는다. 광고비 내림차순.
 * `fallbackCurrency` 는 행에 통화가 없을 때(수집 전 stamp 없는 행) 쓴다 — 서버가 이미 샵 통화로 채워 준다.
 */
export function groupRows(
  rows: AdPerformanceAdRow[],
  level: BreakdownLevel,
  fallbackCurrency: string | null = null,
): BreakdownRow[] {
  interface Acc {
    key: string
    name: string | null
    nameDate: string
    parent: string | null
    spend: number
    impressions: number
    clicks: number
    conversions: number
    value: number
    conversionsKnown: boolean
    valueKnown: boolean
    currencies: Set<string>
    /** 통화를 모르는 행(행 stamp·샵 통화 모두 없음)이 있었다 — KRW 로 간주하지 않는다 */
    currencyUnknown: boolean
  }
  const groups = new Map<string, Acc>()
  for (const row of rows) {
    const key = levelId(row, level) ?? UNKNOWN_KEY
    let g = groups.get(key)
    if (!g) {
      g = {
        key,
        name: null,
        nameDate: '',
        parent: null,
        spend: 0,
        impressions: 0,
        clicks: 0,
        conversions: 0,
        value: 0,
        conversionsKnown: true,
        valueKnown: true,
        currencies: new Set(),
        currencyUnknown: false,
      }
      groups.set(key, g)
    }
    g.spend += row.spend
    g.impressions += row.impressions
    g.clicks += row.clicks
    if (row.conversions == null) g.conversionsKnown = false
    else g.conversions += row.conversions
    if (row.conversion_value == null) g.valueKnown = false
    else g.value += row.conversion_value
    const cur = row.currency ?? fallbackCurrency
    if (cur) g.currencies.add(cur)
    else g.currencyUnknown = true
    // 이름은 가장 최근 날 것 — 'YYYY-MM-DD' 는 문자열 비교로 순서가 맞다.
    const name = levelName(row, level)
    if (name && row.date >= g.nameDate) {
      g.name = name
      g.nameDate = row.date
      if (level !== 'campaign') g.parent = row.campaign_name ?? null
    }
  }

  return [...groups.values()]
    .map((g) => {
      const conversions = g.conversionsKnown ? g.conversions : null
      const conversion_value = g.valueKnown ? g.value : null
      return {
        key: g.key,
        name: g.name ?? (g.key === UNKNOWN_KEY ? '(ID 없음)' : g.key),
        parent: g.parent,
        spend: g.spend,
        impressions: g.impressions,
        clicks: g.clicks,
        conversions,
        conversion_value,
        // 합 → 나눔. 서버 `_metrics` 와 같은 식(분모 0 이면 null).
        roas: conversion_value != null && g.spend > 0 ? conversion_value / g.spend : null,
        ctr: g.impressions > 0 ? (g.clicks / g.impressions) * 100 : null,
        cpc: g.clicks > 0 ? g.spend / g.clicks : null,
        // 하나로 확정될 때만 통화를 싣는다. 모르는 행이 섞여도 null(리뷰 Minor — 섞인 걸 KRW 로 확정하지 않는다).
        currency: g.currencies.size === 1 && !g.currencyUnknown ? [...g.currencies][0] : null,
      }
    })
    .sort((a, b) => b.spend - a.spend || a.name.localeCompare(b.name))
}
